// @flow

import * as React from 'react';
import Modal from 'react-bootstrap/lib/Modal';
import Button from 'react-bootstrap/lib/Button';
import FormControl from 'react-bootstrap/lib/FormControl';
import FormGroup from 'react-bootstrap/lib/FormGroup';
import ControlLabel from 'react-bootstrap/lib/ControlLabel';
import HelpBlock from 'react-bootstrap/lib/HelpBlock';

type Props = {
  show: boolean,
  onHide: () => void,
  onSave: (viewName: string) => void,
  loading: boolean,
  maxLength: number,
  existingNames: string[],
};

type State = {
  viewName: string,
  error: string,
};

class SaveAsNewViewModal extends React.Component<Props, State> {
  inputRef: { current: null | HTMLInputElement };

  constructor(props: Props) {
    super(props);
    this.state = {
      viewName: '',
      error: '',
    };
    this.inputRef = React.createRef();
  }

  componentDidUpdate(prevProps: Props) {
    // Reset and focus when modal opens
    if (this.props.show && !prevProps.show) {
      this.setState({ viewName: '', error: '' });
      setTimeout(() => {
        if (this.inputRef.current) {
          this.inputRef.current.focus();
        }
      }, 100);
    }
  }

  validate = (name: string): string => {
    const trimmed = name.trim();
    if (!trimmed) {
      return 'Please enter a view name';
    }
    if (trimmed.length > this.props.maxLength) {
      return `Name must be ${this.props.maxLength} characters or less`;
    }
    if (this.props.existingNames.some(n => n.toLowerCase() === trimmed.toLowerCase())) {
      return 'A view with this name already exists';
    }
    return '';
  };

  handleSubmit = (e: Event) => {
    e.preventDefault();
    const validationError = this.validate(this.state.viewName);
    if (validationError) {
      this.setState({ error: validationError });
      return;
    }
    this.props.onSave(this.state.viewName.trim());
  };

  handleChange = (e: SyntheticInputEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const { error } = this.state;
    this.setState({
      viewName: value,
      error: error ? this.validate(value) : '',
    });
  };

  render() {
    const { viewName, error } = this.state;
    const { props } = this;
    const hasError = error !== '';
    const charsRemaining = props.maxLength - viewName.length;

    return (
      <Modal show={props.show} onHide={props.onHide} bsSize="small">
        <form onSubmit={this.handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>Save as New View</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <FormGroup validationState={hasError ? 'error' : null}>
              <ControlLabel>View Name</ControlLabel>
              <FormControl
                type="text"
                value={viewName}
                onChange={this.handleChange}
                placeholder="e.g., Monthly Review"
                maxLength={props.maxLength}
                inputRef={ref => { this.inputRef.current = ref; }}
                disabled={props.loading}
              />
              {hasError ? (
                <HelpBlock>{error}</HelpBlock>
              ) : (
                <HelpBlock>{charsRemaining} characters remaining</HelpBlock>
              )}
            </FormGroup>
          </Modal.Body>

          <Modal.Footer>
            <Button onClick={props.onHide} disabled={props.loading}>
              Cancel
            </Button>
            <Button
              bsStyle="primary"
              type="submit"
              disabled={props.loading || viewName.trim() === ''}
            >
              {props.loading ? 'Saving...' : 'Save View'}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>
    );
  }
}

export default SaveAsNewViewModal;
